// Pestanya "Calendari" de Gestió d'un Empleat: vista global de només
// lectura (CURRENT_TASK.md, punts 9-10) que combina en un mateix calendari
// l'Horari vigent, els Festius del Calendari Laboral de l'Empresa, les
// Absències i les Baixes. Cada dia pot mostrar diverses capes alhora (no
// se'n substitueix cap): el tipus de dia (Festiu/No laborable/Laborable) es
// marca amb una vora de color i les Absències/Baixes amb xips de color,
// sense pintar mai tota la cel·la amb un únic color.
//
// Dues vistes, igual que a Absències: Mes (per defecte) o Any complet (els
// 12 mesos de cop). Es carrega sempre l'Any complet una sola vegada des del
// backend (chResoldreRangEmpleat resol tot l'interval amb un nombre fix de
// consultes): navegar de mes o canviar de vista no torna a demanar dades al
// servidor, només canviar d'Any ho fa.
//
// Reutilitza NOMS_MESOS/DIES_SETMANA/generarDiesMes/formatDataISO/
// formatTramsDia de HorariEditor.js, igual que AbsenciesEmpleat.js, en
// comptes de duplicar-les.

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { formatDate } from "../../utils/Utilitats";
import { API_URL } from "../../utils/config";
import { NOMS_MESOS, DIES_SETMANA, generarDiesMes, formatDataISO, formatHoraCurta, formatTramsDia } from "./HorariEditor";
import "./calendariempleat.css";

function CalendariEmpleat({ idEmpleat, idEmpresa, onNavegarPestanya }) {
  const avui = new Date();
  const [any, setAny] = useState(avui.getFullYear());
  const [mes, setMes] = useState(avui.getMonth()); // 0-11
  const [vista, setVista] = useState("mes"); // "mes" | "any"

  const [dies, setDies] = useState([]);
  const [primeraAlta, setPrimeraAlta] = useState(null); // "YYYY-MM-DD" | null
  const [tipusOptions, setTipusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const carregarTipus = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const res = await fetchAutenticat(`${API_URL}/TipusAbsencia.php?idEmpresa=${idEmpresa}`);
      const data = await res.json();
      if (data.success) setTipusOptions(data.tipus || []);
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) console.error("Error carregant Tipus d'absència:", e);
    }
  }, [idEmpresa]);

  // Carrega sempre l'Any complet (mai només el Mes): navegar de mes dins el
  // mateix any, o canviar entre vista "Mes"/"Any complet", no torna a
  // demanar dades al servidor.
  const carregarDies = useCallback(async () => {
    if (!idEmpleat || !idEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ idEmpleat, idEmpresa, Any: any });
      const res = await fetchAutenticat(`${API_URL}/CalendariEmpleat.php?${params.toString()}`);
      const data = await res.json();
      if (data.success) {
        setDies(data.dies || []);
        setPrimeraAlta(data.primeraAlta || null);
      } else {
        setError(data.message || "No s'ha pogut carregar el Calendari.");
        setDies([]);
      }
    } catch (e) {
      if (!(e instanceof SessioCaducadaError)) setError("Error de connexió amb el servidor.");
    } finally {
      setLoading(false);
    }
  }, [idEmpleat, idEmpresa, any]);

  useEffect(() => {
    carregarTipus();
  }, [carregarTipus]);

  useEffect(() => {
    carregarDies();
  }, [carregarDies]);

  const diesPerData = useMemo(() => {
    const mapa = {};
    dies.forEach((d) => {
      mapa[d.Data] = d;
    });
    return mapa;
  }, [dies]);

  const tipusActius = useMemo(
    () => tipusOptions.filter((t) => Number(t.Actiu) === 1),
    [tipusOptions]
  );

  const canviarMes = (delta) => {
    let novaMes = mes + delta;
    let novaAny = any;
    if (novaMes < 0) {
      novaMes = 11;
      novaAny -= 1;
    } else if (novaMes > 11) {
      novaMes = 0;
      novaAny += 1;
    }
    setMes(novaMes);
    setAny(novaAny);
  };

  // Límit de navegació cap enrere (CURRENT_TASK_RECTIFICATIU_VIGENCIA_
  // EMPLEAT.md §9.2): no es pot recórrer indefinidament cap a anys
  // anteriors a la primera Alta coneguda com si fossin anys laborals
  // normals. No es limita la navegació cap endavant (§9.3: es prefereix no
  // bloquejar-la, per si hi ha un reingrés futur).
  const anyMinim = primeraAlta ? Number(primeraAlta.slice(0, 4)) : null;
  const potAnarEnrere = !anyMinim || (vista === "any" ? any > anyMinim : (any > anyMinim || mes > 0));

  const navegarAnterior = () => {
    if (!potAnarEnrere) return;
    if (vista === "mes") canviarMes(-1);
    else setAny((a) => a - 1);
  };
  const navegarSeguent = () => (vista === "mes" ? canviarMes(1) : setAny((a) => a + 1));

  const construirTitol = (dia) => {
    const linies = [formatDate(dia.Data)];
    if (dia.ForaVigencia) {
      linies.push("Encara no estava d'alta / fora de vigència");
    } else {
      linies.push(dia.Festiu ? (dia.TipusFestiu === "EMPRESA" ? "Festiu d'Empresa" : "Festiu") : (dia.TramsPrevists.length > 0 ? formatTramsDia(dia.TramsPrevists) : "No laborable segons l'Horari"));
    }
    if (dia.Baixa) linies.push(`Baixa: ${dia.Baixa.TipusNom}`);
    if (dia.AbsenciaCompleta) linies.push(`Absència: ${dia.AbsenciaCompleta.TipusNom}`);
    dia.AbsenciesParcials.forEach((a) => {
      linies.push(`${a.TipusNom}: ${formatHoraCurta(a.HoraInici)}–${formatHoraCurta(a.HoraFinal)}`);
    });
    return linies.join("\n");
  };

  // Una cel·la de dia: en vista "any" es mostra en format compacte (punts de
  // color en comptes de xips de text), perquè hi càpiguen els 12 mesos de cop.
  const renderDiaCella = (anyCel, mesCel, diaNum, index, compacte) => {
    if (diaNum === null) {
      return (
        <div
          key={index}
          className={`calendari-emp-dia calendari-emp-dia-buit${compacte ? " calendari-emp-dia-petit" : ""}`}
        />
      );
    }
    const dataISO = formatDataISO(anyCel, mesCel, diaNum);
    const dia = diesPerData[dataISO];

    const classes = ["calendari-emp-dia"];
    if (compacte) classes.push("calendari-emp-dia-petit");
    if (dia) {
      // Fora de vigència (abans de l'Alta / després de la Baixa) es grisa
      // diferent d'un simple dia no laborable com un cap de setmana
      // (CURRENT_TASK_RECTIFICATIU_VIGENCIA_EMPLEAT.md §9.1).
      if (dia.ForaVigencia) classes.push("calendari-emp-dia-fora-vigencia");
      else if (dia.Festiu) classes.push(dia.TipusFestiu === "EMPRESA" ? "calendari-emp-dia-festiu-empresa" : "calendari-emp-dia-festiu");
      else if (!dia.TramsPrevists || dia.TramsPrevists.length === 0) classes.push("calendari-emp-dia-no-laborable");
      else classes.push("calendari-emp-dia-laborable");
    }

    const xips = [];
    if (dia?.Baixa) {
      xips.push({ key: `b-${dia.Baixa.idBaixesLaborals}`, text: `Baixa: ${dia.Baixa.TipusNom}`, color: dia.Baixa.TipusColor });
    }
    if (dia?.AbsenciaCompleta) {
      xips.push({ key: `ac-${dia.AbsenciaCompleta.idAbsenciesEmpleats}`, text: dia.AbsenciaCompleta.TipusNom, color: dia.AbsenciaCompleta.TipusColor });
    }
    (dia?.AbsenciesParcials || []).forEach((a) => {
      xips.push({
        key: `ap-${a.idAbsenciesEmpleats}`,
        text: `${a.TipusNom} (${formatHoraCurta(a.HoraInici)}–${formatHoraCurta(a.HoraFinal)})`,
        color: a.TipusColor,
      });
    });

    return (
      <div key={index} className={classes.join(" ")} title={dia ? construirTitol(dia) : ""}>
        <div className="calendari-emp-dia-num">{diaNum}</div>
        {xips.length > 0 && (
          compacte ? (
            <div className="calendari-emp-dia-punts">
              {xips.slice(0, 4).map((x) => (
                <span key={x.key} className="calendari-emp-punt" style={{ backgroundColor: x.color || "#999999" }} />
              ))}
            </div>
          ) : (
            <div className="calendari-emp-dia-xips">
              {xips.slice(0, 3).map((x) => (
                <span key={x.key} className="calendari-emp-xip" style={{ backgroundColor: x.color || "#999999" }}>
                  {x.text}
                </span>
              ))}
              {xips.length > 3 && <span className="calendari-emp-xip-mes">+{xips.length - 3}</span>}
            </div>
          )
        )}
      </div>
    );
  };

  const renderGraellaMes = (anyMes, mesIdx, compacte) => (
    <div className={`calendari-emp-mes-bloc${compacte ? " calendari-emp-mes-bloc-petit" : ""}`} key={`${anyMes}-${mesIdx}`}>
      {compacte && <div className="calendari-emp-mes-titol">{NOMS_MESOS[mesIdx]}</div>}
      <div className="calendari-emp-dies-setmana">
        {DIES_SETMANA.map((d) => (
          <div key={d.valor} className="calendari-emp-dia-setmana">{d.curt}</div>
        ))}
      </div>
      <div className="calendari-emp-graella">
        {generarDiesMes(anyMes, mesIdx).map((diaNum, index) => renderDiaCella(anyMes, mesIdx, diaNum, index, compacte))}
      </div>
    </div>
  );

  if (!idEmpleat || !idEmpresa) {
    return <p>Falten dades (Empleat o Empresa) per carregar el Calendari.</p>;
  }

  return (
    <div className="calendari-emp">
      <div className="calendari-emp-vista-selector">
        <button type="button" className={vista === "mes" ? "active" : ""} onClick={() => setVista("mes")}>Mes</button>
        <button type="button" className={vista === "any" ? "active" : ""} onClick={() => setVista("any")}>Any complet</button>
      </div>

      <div className="calendari-emp-nav">
        <button type="button" className="dv2-btn dv2-btn--icon" onClick={navegarAnterior} disabled={!potAnarEnrere}>‹</button>
        <span className="calendari-emp-titol-mes">{vista === "mes" ? `${NOMS_MESOS[mes]} ${any}` : any}</span>
        <button type="button" className="dv2-btn dv2-btn--icon" onClick={navegarSeguent}>›</button>
      </div>

      {onNavegarPestanya && (
        <div className="calendari-emp-accions">
          <button type="button" className="dv2-btn" onClick={() => onNavegarPestanya("horari")}>Veure Horari</button>
          <button type="button" className="dv2-btn" onClick={() => onNavegarPestanya("absencies")}>Veure Absències</button>
          <button type="button" className="dv2-btn" onClick={() => onNavegarPestanya("baixes")}>Veure Baixes</button>
        </div>
      )}

      {loading && <p>Carregant Calendari...</p>}
      {error && <p className="calendari-emp-error">{error}</p>}

      {!loading && !error && (
        <div className="calendari-emp-calendari">
          {vista === "mes" ? (
            renderGraellaMes(any, mes, false)
          ) : (
            <div className="calendari-emp-any-graella">
              {NOMS_MESOS.map((_, idx) => renderGraellaMes(any, idx, true))}
            </div>
          )}
        </div>
      )}

      <div className="calendari-emp-llegenda">
        <span className="calendari-emp-llegenda-item">
          <span className="calendari-emp-llegenda-quadre calendari-emp-llegenda-festiu" /> Festiu
        </span>
        <span className="calendari-emp-llegenda-item">
          <span className="calendari-emp-llegenda-quadre calendari-emp-llegenda-festiu-empresa" /> Festiu d'Empresa
        </span>
        <span className="calendari-emp-llegenda-item">
          <span className="calendari-emp-llegenda-quadre calendari-emp-llegenda-no-laborable" /> No laborable
        </span>
        <span className="calendari-emp-llegenda-item">
          <span className="calendari-emp-llegenda-quadre calendari-emp-llegenda-fora-vigencia" /> Fora de vigència
        </span>
        {tipusActius.map((t) => (
          <span key={t.idTipusAbsenciaEmpleats} className="calendari-emp-llegenda-item">
            <span className="calendari-emp-llegenda-quadre" style={{ backgroundColor: t.Color || "#999999" }} /> {t.Nom}
          </span>
        ))}
      </div>
    </div>
  );
}

export default CalendariEmpleat;
